import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, itDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, AlertTriangle, CalendarCheck, Trash2, BellRing } from "lucide-react";

export default function Manutenzioni() {
  const [params] = useSearchParams();
  const plantFilter = params.get("plant");
  const [rows, setRows] = useState([]);
  const [plants, setPlants] = useState([]);
  const [techs, setTechs] = useState([]);
  const [open, setOpen] = useState(false);
  const [planning, setPlanning] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reminders/run");
      setMsg(`Avvisi inviati: ${data.manutenzioni} manutenzioni, ${data.contratti} contratti, ` +
             `riepilogo ufficio con ${data.ufficio} righe.`);
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const load = () => api.get("/maintenance").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/plants").then(({ data }) => setPlants(data));
    api.get("/users", { params: { role: "tecnico" } }).then(({ data }) => setTechs(data)).catch(() => {});
  }, []);

  const createFields = [
    {
      name: "plant_id", label: "Impianto", type: "select", required: true, full: true,
      options: plants.map((p) => ({ value: p.id, label: `${p.name} — ${p.customer_name}` })),
    },
    { name: "scheduled_date", label: "Data prevista", type: "date", required: true },
    { name: "frequency_months", label: "Frequenza (mesi)", type: "number" },
    { name: "notes", label: "Note", type: "textarea", full: true },
  ];

  const planFields = [
    {
      name: "technician_id", label: "Tecnico assegnato", type: "select", full: true,
      options: techs.map((t) => ({ value: t.id, label: t.name })),
    },
    { name: "scheduled_date", label: "Data intervento", type: "date" },
    { name: "scheduled_time", label: "Ora", type: "time" },
  ];

  const save = async (values) => {
    try {
      await api.post("/maintenance", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const plan = async (values) => {
    try {
      const { data } = await api.post(`/maintenance/${planning.id}/plan`, values);
      setMsg(`Creato intervento ${data.code} dalla scadenza.`);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="manutenzioni-page">
      <PageHeader title="Manutenzioni programmate" subtitle={`${rows.length} scadenze`}>
        <Button variant="outline" className="h-11 rounded-sm" onClick={sendReminders} disabled={sending}
                data-testid="send-reminders-btn">
          <BellRing className="h-4 w-4 mr-2" /> {sending ? "Invio…" : "Invia avvisi scadenze"}
        </Button>
        <Button className="h-11 rounded-sm" onClick={() => setOpen(true)} data-testid="new-maintenance-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuova scadenza
        </Button>
      </PageHeader>

      {msg && <p className="mb-4 text-sm font-medium text-primary" data-testid="maintenance-message">{msg}</p>}

      <ListCard testid="manutenzioni-list">
        {rows.map((m) => (
          <div key={m.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold">{m.plant_name || m.customer_name || "Impianto rimosso"}</p>
                <Badge className="rounded-sm capitalize" variant="secondary">{m.status}</Badge>
                {m.overdue && (
                  <Badge className="rounded-sm bg-destructive text-destructive-foreground">
                    <AlertTriangle className="h-3 w-3 mr-1" /> scaduta
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {m.customer_name} · prevista {itDate(m.scheduled_date)} · ogni {m.frequency_months} mesi
                {m.notes ? ` · ${m.notes}` : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-sm" onClick={() => setPlanning(m)}
                      data-testid={`plan-maintenance-${m.id}`} disabled={m.status !== "programmata"}>
                <CalendarCheck className="h-3.5 w-3.5 mr-2" /> Pianifica
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                      onClick={async () => { await api.delete(`/maintenance/${m.id}`); load(); }}
                      data-testid={`delete-maintenance-${m.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty text="Nessuna manutenzione programmata." />}
      </ListCard>

      <FormDialog open={open} onOpenChange={setOpen} title="Nuova scadenza di manutenzione"
                  fields={createFields}
                  initial={{ frequency_months: 6, ...(plantFilter ? { plant_id: plantFilter } : {}) }}
                  onSubmit={save} testid="maintenance-dialog" />
      <FormDialog open={!!planning} onOpenChange={(v) => !v && setPlanning(null)}
                  title="Pianifica intervento" fields={planFields}
                  initial={{ scheduled_date: planning?.scheduled_date, scheduled_time: "09:00" }}
                  onSubmit={plan} testid="plan-dialog" />
    </div>
  );
}
